import React, { useState, useEffect, useCallback, memo, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  TextField,
  CircularProgress,
  Alert,
  Collapse,
  FormControlLabel,
  MenuItem,
  Autocomplete,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  InputAdornment,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { supabase } from '../../supabaseClient';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Switch } from '@mui/material';
import { useDomainValues } from '../Lookup_components/document_domainvalueslookup';
import debounce from 'lodash/debounce';
import { v4 as uuidv4 } from 'uuid';
import ClearIcon from '@mui/icons-material/Clear';

// Verplaats deze component naar buiten en gebruik memo
// const AutocompleteField = memo(({ label, value, onChange, options, loading }) => {
//   const [open, setOpen] = useState(false);
//   const currentOption = useMemo(() => 
//     options?.find(option => option.domain_value_id === value) || null,
//     [options, value]
//   ); 
// 
//   return (
//     <Autocomplete
//       fullWidth
//       open={open}
//       onOpen={() => setOpen(true)}
//       onClose={() => setOpen(false)}
//       options={options || []}
//       getOptionLabel={(option) => option.description || ''}
//       value={currentOption}
//       onChange={(event, newValue) => {
//         onChange(newValue?.domain_value_id || null);
//       }}
//       renderInput={(params) => (
//         <TextField 
//           {...params} 
//           label={label} 
//           fullWidth
//           margin="normal"
//         />
//       )}
//       loading={loading}
//       loadingText="Opties laden..."
//       noOptionsText="Geen resultaten"
//     />
//   );
// });

// Optimized AutocompleteField component (copied from ProjectInfoTab.jsx, adapted for DrillPointsTab usage)
const AutocompleteField = memo(({ 
  label, 
  value, // expects domain_value_id directly
  onChange, // expects function(newValueId)
  options, // expects array of { domain_value_id, description, ... }
  loading,
  isReadOnly
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  
  // Added check to ensure options is defined and an array
  const filteredOptions = useMemo(() => {
    if (!options || !Array.isArray(options)) return [];

    // Don't sort if the label contains "eenheid" (case insensitive)
    const isUnitField = label.toLowerCase().includes('eenheid');
    
    // Create a sorted or unsorted copy of the options array
    let processedOptions = isUnitField ? [...options] : [...options].sort((a, b) => 
      (a.description || '').localeCompare(b.description || '')
    );
    
    if (!inputValue || inputValue.length < 1) {
      return processedOptions;
    }
    // Filter based on description containing inputValue
    return processedOptions.filter(option => 
      option.description?.toLowerCase().includes(inputValue.toLowerCase())
    );
  }, [inputValue, options, label]); // Added label as dependency

  // Find the currently selected option based on the value (domain_value_id)
  const currentOption = useMemo(() => 
    (options || []).find(option => option.domain_value_id === value) || null,
    [options, value]
  );
  
  // Handle input changes for filtering, but not on selection
  const handleInputChange = useCallback(debounce((event, newInputValue, reason) => {
    if (reason === 'input' || reason === 'clear') {
        setInputValue(newInputValue);
    } 
  }, 50), []);
  
  // Handle empty or missing options
  if (!options || options.length === 0) {
    return (
      <TextField
        fullWidth
        label={label}
        value=""
        disabled
        margin="normal" // Added margin consistent with old component
        helperText={`Geen opties beschikbaar voor ${label}`}
      />
    );
  }

  // Render the Autocomplete component
  return (
    <Autocomplete
      fullWidth
      disableCloseOnSelect // Keep open after selection
      // disableListWrap // Removed as ListboxComponent is not used here
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={filteredOptions} // Use filtered options
      getOptionLabel={(option) => option.description || ''} // Display description
      isOptionEqualToValue={(option, val) => option.domain_value_id === val?.domain_value_id}
      value={currentOption} // The currently selected option object
      onChange={(event, newValue) => {
        // Call the parent onChange with the new domain_value_id or null
        onChange(newValue?.domain_value_id || null); 
      }}
      renderOption={(liProps, option) => { // Render options with truncation
        const { key, ...restLiProps } = liProps;
        return (
          <li
            key={key}
            {...restLiProps}
            style={{
              ...restLiProps.style,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block'
            }}
          >
            {option.description || ''} {/* Display description */} 
          </li>
        );
      }}
      onInputChange={handleInputChange} // Handle input for filtering
      renderInput={(params) => ( // Render the input field
        <TextField 
          {...params} 
          label={label} 
          fullWidth
          margin="normal" // Added margin consistent with old component
          disabled={isReadOnly}
        />
      )}
      loading={loading && filteredOptions.length === 0} // Show loading indicator
      loadingText="Opties laden..."
      noOptionsText="Geen resultaten"
      // ListboxComponent={ListboxComponent} // Removed as not used here
      // ListboxProps={{ // Removed as not used here
      //   style: { maxHeight: 300 }
      // }}
      PopperProps={{ // Force dropdown to open downwards
        placement: 'bottom-start'
      }}
    />
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for memoization
  return (
    prevProps.value === nextProps.value &&
    prevProps.loading === nextProps.loading &&
    // Simple comparison, might need deep comparison if options structure is complex
    prevProps.options === nextProps.options 
  );
});

// Separate component for Drillmaster to manage its own state for advanced Autocomplete features
const DrillmasterAutocompleteField = memo(({ point, persons, onSave, isReadOnly }) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const currentPerson = useMemo(() => 
    persons.find(p => p.id === point.drillmaster) || null,
    [persons, point.drillmaster]
  );

  const filteredPersons = useMemo(() => {
    if (!inputValue) return persons;
    const lowerInputValue = inputValue.toLowerCase();
    return persons.filter(p => 
      (`${p.firstname || ''} ${p.lastname || ''}`.trim().toLowerCase().includes(lowerInputValue))
    );
  }, [persons, inputValue]);

  const handleInputChange = useCallback(debounce((event, newInputValue, reason) => {
    if (reason === 'input' || reason === 'clear') {
      setInputValue(newInputValue);
    }
  }, 50), []);

  const handleChange = (event, newValue) => {
    const updatedPoint = { ...point, drillmaster: newValue?.id || null };
    // We don't call setDrillPoints here, we call the onSave prop
    onSave(updatedPoint);
  };

  return (
    <Autocomplete
      fullWidth
      disableCloseOnSelect
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={filteredPersons}
      getOptionLabel={(option) => `${option.firstname || ''} ${option.lastname || ''}`.trim()}
      value={currentPerson}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      onChange={handleChange}
      onInputChange={handleInputChange}
      disabled={isReadOnly}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Drillmaster"
          fullWidth
          margin="normal"
          disabled={isReadOnly}
        />
      )}
      renderOption={(liProps, option) => {
        const { key, ...restLiProps } = liProps;
        const label = `${option.firstname || ''} ${option.lastname || ''}`.trim();
        return (
          <li 
            key={option.id}
            {...restLiProps} 
            style={{
              ...restLiProps.style,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block'
            }}
          >
            {label}
          </li>
        );
      }}
    />
  );
});

export default function DrillPointsTab({ document, isReadOnly }) {
  const [drillPoints, setDrillPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedItem, setExpandedItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [persons, setPersons] = useState([]);
  // New state for create/delete functionality
  const [isCreating, setIsCreating] = useState(false);
  const [newDrillPoint, setNewDrillPoint] = useState({
    name: '',
    project_id: null,
    document_id: document.id
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [projectId, setProjectId] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [drillPointToDelete, setDrillPointToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const technicalNames = useMemo(() => [
    'MeetObjectSoort',
    'MaaiveldType',
    'Hoedanigheid'
  ], []);
  const { domainValues, loading: domainValuesLoading, error: domainValuesError } = useDomainValues(technicalNames);

  // Fetch specific units for length
  const { 
    domainValues: lengthUnitsData, 
    loading: lengthUnitsLoading, 
    error: lengthUnitsError 
  } = useDomainValues({ technical_name: 'Eenheid', unit_dimension: 'l' });

  // Add new hook call for ReferentieVlak
  const {
    domainValues: referentieVlakData,
    loading: referentieVlakLoading,
    // error: referentieVlakError // Optional: handle error
  } = useDomainValues({ technical_name: 'Hoedanigheid', group_name: 'ReferentieVlak', sortByFrequency: true });

  // Find the domain_value_id for 'meter'
  const meterUnitId = useMemo(() => {
    const units = lengthUnitsData?.['Eenheid'];
    if (!units) return null;
    const meterOption = units.find(unit => unit.description?.toLowerCase() === 'meter');
    return meterOption?.domain_value_id || null;
  }, [lengthUnitsData]);

  // Find the domain_value_id for 't.o.v. maaiveld'
  const defaultConditionId = useMemo(() => {
    const conditions = referentieVlakData?.['Hoedanigheid'];
    if (!conditions) return null;
    const conditionOption = conditions.find(cond => cond.description?.toLowerCase() === 't.o.v. maaiveld');
    return conditionOption?.domain_value_id || null;
  }, [referentieVlakData]);

  useEffect(() => {
    // Only fetch if document exists, necessary data is loaded, and meterUnitId/defaultConditionId are resolved (even if null)
    if (document?.id && !lengthUnitsLoading && !domainValuesLoading && !referentieVlakLoading && meterUnitId !== undefined && defaultConditionId !== undefined) {
      fetchDrillPoints(meterUnitId, defaultConditionId); // Pass both IDs
      fetchPersons();
    }
    // Ensure fetch runs if IDs become available after initial load
  }, [document, lengthUnitsLoading, domainValuesLoading, referentieVlakLoading, meterUnitId, defaultConditionId]); // Add dependencies

  const fetchDrillPoints = async (meterUnitId, defaultConditionId) => { // Accept both IDs
    setLoading(true); // Set loading at the start
    setError(null);
    try {
      const { data: projectData, error: projectError } = await supabase
        .from('document_project')
        .select('id')
        .eq('document_id', document.id)
        .single();

      if (projectError) throw projectError;

      setProjectId(projectData.id);

      const { data, error } = await supabase
        .from('document_borehole')
        .select('*')
        .eq('project_id', projectData.id);

      if (error) throw error;

      let processedData = data || [];

      // Set default unit/condition if null and save to DB
      if ((meterUnitId || defaultConditionId) && data && data.length > 0) {
        const updatePromises = [];
        processedData = data.map(point => {
          let updates = {};
          let needsUpdate = false;
          let updatedPoint = { ...point };

          if (meterUnitId && point.depth_uom == null) { // Check depth unit
            updates.depth_uom = meterUnitId;
            updatedPoint.depth_uom = meterUnitId;
            needsUpdate = true;
          }
          if (meterUnitId && point.groundLevel_uom == null) { // Check ground level unit
            updates.groundLevel_uom = meterUnitId;
            updatedPoint.groundLevel_uom = meterUnitId;
            needsUpdate = true;
          }
          if (defaultConditionId && point.depth_condition == null) { // Check depth condition
            updates.depth_condition = defaultConditionId;
            updatedPoint.depth_condition = defaultConditionId;
            needsUpdate = true;
          }

          if (needsUpdate) {
            updatePromises.push(
              supabase
                .from('document_borehole')
                .update(updates)
                .eq('id', point.id)
            );
          }
          return updatedPoint;
        });

        if (updatePromises.length > 0) {
          console.log(`Attempting to set default values for ${updatePromises.length} borehole fields...`);
          const results = await Promise.allSettled(updatePromises);
          let updateErrors = 0;
          results.forEach((result, index) => {
            if (result.status === 'rejected') {
              updateErrors++;
              console.error(`Failed to update default value for borehole (index ${index}):`, result.reason);
            }
          });
          if (updateErrors > 0) {
             setError(prev => {
                const errorMsg = `Kon voor ${updateErrors} veld(en) de standaard waarde (eenheid/conditie) niet opslaan.`; // Updated error message
                return prev ? `${prev}\n${errorMsg}` : errorMsg;
             });
          } else if (updatePromises.length > 0) {
              console.log("Default value updates successful.");
          }
        }
      }

      // Natuurlijke sortering implementeren in JavaScript (on processedData)
      const sortedData = [...processedData].sort((a, b) => {
        // Zet null/undefined namen onderaan
        if (!a.name) return 1;
        if (!b.name) return -1;
        
        // Natuurlijke sortering voor strings met nummers
        return a.name.localeCompare(b.name, undefined, {
          numeric: true,
          sensitivity: 'base'
        });
      });

      setDrillPoints(sortedData);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPersons = async () => {
    try {
      const { data, error } = await supabase
        .from('document_person')
        .select('id, firstname, lastname')
        .eq('document_id', document.id);
      
      if (error) throw error;
      setPersons(data || []);
    } catch (error) {
      console.error('Error fetching persons:', error);
    }
  };

  const validateDomainValue = (value, technicalName) => {
    if (!value) return true; // null/empty values are allowed
    const validValues = domainValues[technicalName] || [];
    return validValues.some(domain => domain.domain_value_id === value);
  };

  // Use a debounced save function similar to ProjectInfoTab
  const debouncedSave = useCallback(
    debounce(async (updatedPoint) => {
      if (isReadOnly) return;
      setSaving(true);
      try {
        const { error } = await supabase
          .from('document_borehole')
          .update(updatedPoint)
          .eq('id', updatedPoint.id);

        if (error) {
          console.error('Database error:', error);
          setError(`Fout bij opslaan: ${error.message}`);
        }
      } catch (err) {
        console.error('Save error:', err);
        setError(`Fout bij opslaan: ${err.message}`);
      } finally {
        setSaving(false);
      }
    }, 500),
    [isReadOnly]
  );

  const handleTextFieldChange = useCallback((point, fieldName, value) => {
    if (isReadOnly) return;
    // Create an updatedPoint object with the new value
    const updatedPoint = { ...point, [fieldName]: value };
    
    // Set local state for immediate UI feedback
    setDrillPoints(prev => prev.map(p => 
      p.id === point.id ? updatedPoint : p
    ));
    
    // Use debounced save for the database update
    debouncedSave(updatedPoint);
  }, [debouncedSave, isReadOnly]);

  const handleDateChange = useCallback((point, date) => {
    if (isReadOnly) return;
    const updatedPoint = { ...point, starttime: date?.toISOString() };
    setDrillPoints(prev => prev.map(p => 
      p.id === point.id ? updatedPoint : p
    ));
    debouncedSave(updatedPoint);
  }, [debouncedSave, isReadOnly]);

  const handleSwitchChange = useCallback((point, checked) => {
    if (isReadOnly) return;
    const updatedPoint = { ...point, isSuccessful: checked };
    setDrillPoints(prev => prev.map(p => 
      p.id === point.id ? updatedPoint : p
    ));
    debouncedSave(updatedPoint);
  }, [debouncedSave, isReadOnly]);

  const isValidDomainValue = (value, technicalName) => {
    if (!value) return true;
    if (!domainValues[technicalName]) return false;
    return domainValues[technicalName].some(
      option => option.domain_value_id === value
    );
  };

  const renderField = useCallback((point, fieldName, technicalName, label) => {
    // Special case for drillmaster field
    if (fieldName === 'drillmaster') {
      // Use the new dedicated component
      return (
        <DrillmasterAutocompleteField 
          key={`${point.id}-drillmaster`}
          point={point}
          persons={persons}
          onSave={(updatedPoint) => {
            if (isReadOnly) return;
             // Update local state immediately for feedback
             setDrillPoints(prev => prev.map(p => 
               p.id === point.id ? updatedPoint : p
             ));
             // Trigger the debounced save
             debouncedSave(updatedPoint);
          }}
          isReadOnly={isReadOnly}
        />
      );
    }

    // Original renderField logic for other fields
    let options;
    // Determine options based on field name
    if (fieldName === 'depth_uom' || fieldName === 'groundLevel_uom') {
      // Use the specifically fetched length units
      options = lengthUnitsData['Eenheid'] || []; 
    } else if (fieldName === 'depth_condition') { // NEW: Use filtered data for depth_condition
      options = referentieVlakData?.['Hoedanigheid'] || [];
    } else {
      // Use the general domain values for other fields
      options = domainValues[technicalName] || [];
    }

    const handleFieldChange = (newValueId) => { 
      const updatedPoint = { ...point, [fieldName]: newValueId };
      setDrillPoints(prev => prev.map(p => 
        p.id === point.id ? updatedPoint : p
      ));
      debouncedSave(updatedPoint);
    };

    return (
      <AutocompleteField
        key={`${point.id}-${fieldName}`}
        label={label}
        value={point[fieldName]}
        onChange={handleFieldChange}
        options={options}
        loading={domainValuesLoading || lengthUnitsLoading || saving || referentieVlakLoading}
        isReadOnly={isReadOnly}
      />
    );
  }, [domainValues, lengthUnitsData, domainValuesLoading, lengthUnitsLoading, saving, persons, debouncedSave, referentieVlakData, referentieVlakLoading, isReadOnly]);

  // Handle creating a new drill point
  const handleOpenCreateDialog = () => {
    if (isReadOnly) return;
    setNewDrillPoint({
      name: '',
      project_id: projectId,
      document_id: document.id
    });
    setIsCreating(true);
  };

  const handleCloseCreateDialog = () => {
    setIsCreating(false);
  };

  const handleCreateDrillPoint = async () => {
    if (isReadOnly) return;
    try {
      setIsSubmitting(true);
      
      const createData = {
        id: uuidv4(),
        ...newDrillPoint,
      };
      
      const { data, error } = await supabase
        .from('document_borehole')
        .insert([createData])
        .select();
      
      if (error) throw error;
      
      setIsCreating(false);
      
      // Refresh drill points
      await fetchDrillPoints(meterUnitId, defaultConditionId); // Pass IDs
      
      // Expand the newly created item
      if (data && data.length > 0) {
        setExpandedItem(data[0].id);
      }
      
    } catch (error) {
      console.error('Error creating drill point:', error);
      setError(`Fout bij aanmaken boring: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle deleting a drill point
  const handleOpenDeleteDialog = (point) => {
    if (isReadOnly) return;
    setDrillPointToDelete(point);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteDrillPoint = async () => {
    if (isReadOnly || !drillPointToDelete) return;
    
    try {
      setIsDeleting(true);
      
      const { error } = await supabase
        .from('document_borehole')
        .delete()
        .eq('id', drillPointToDelete.id);
      
      if (error) throw error;
      
      // Close dialog and reset state
      setIsDeleteDialogOpen(false);
      setDrillPointToDelete(null);
      
      // Refresh drill points
      await fetchDrillPoints(meterUnitId, defaultConditionId); // Pass IDs
      
      // Clear expanded item if it was deleted
      if (expandedItem === drillPointToDelete.id) {
        setExpandedItem(null);
      }
      
    } catch (error) {
      console.error('Error deleting drill point:', error);
      setError(`Fout bij verwijderen boring: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  // Combine loading states
  const combinedLoading = loading || domainValuesLoading || lengthUnitsLoading || referentieVlakLoading; // Added referentieVlakLoading
  // Combine error states (simple approach: show first error)
  const combinedError = error || domainValuesError || lengthUnitsError; // Consider adding referentieVlakError

  if (combinedLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Empty state with create button
  if (drillPoints.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Boringen</Typography>
        
        {/* Display combined error */} 
        {combinedError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {combinedError}
          </Alert>
        )}
        
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography sx={{ mb: 2 }}>Geen boringen gevonden</Typography>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
            disabled={isReadOnly}
          >
            Nieuwe Boring
          </Button>
          
          {/* Create Dialog */}
          <Dialog 
            open={isCreating} 
            onClose={handleCloseCreateDialog} 
            maxWidth="sm" 
            fullWidth
            // Add slotProps to make backdrop transparent
            slotProps={{
              backdrop: {
                sx: {
                  backgroundColor: 'transparent',
                },
              },
            }}
          >
            <DialogTitle>Nieuwe Boring</DialogTitle>
            <DialogContent>
              <TextField
                label="Naam"
                value={newDrillPoint.name}
                onChange={(e) => setNewDrillPoint(prev => ({ ...prev, name: e.target.value }))}
                fullWidth
                margin="normal"
                autoFocus
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseCreateDialog}>Annuleren</Button>
              <Button 
                onClick={handleCreateDrillPoint} 
                variant="contained" 
                disabled={isSubmitting || isReadOnly}
                startIcon={isSubmitting ? <CircularProgress size={24} /> : null}
              >
                {isSubmitting ? 'Bezig met aanmaken...' : 'Aanmaken'}
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Boringen</Typography>

      {/* Display combined error */} 
      {combinedError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {combinedError}
        </Alert>
      )}

      {/* Add new boring button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
          disabled={isReadOnly}
        >
          Nieuwe Boring
        </Button>
      </Box>

      {/* Create Dialog */}
      <Dialog 
        open={isCreating} 
        onClose={handleCloseCreateDialog} 
        maxWidth="sm" 
        fullWidth
        // Add slotProps to make backdrop transparent
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'transparent',
            },
          },
        }}
      >
        <DialogTitle>Nieuwe Boring</DialogTitle>
        <DialogContent>
          <TextField
            label="Naam"
            value={newDrillPoint.name}
            onChange={(e) => setNewDrillPoint(prev => ({ ...prev, name: e.target.value }))}
            fullWidth
            margin="normal"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Annuleren</Button>
          <Button 
            onClick={handleCreateDrillPoint} 
            variant="contained" 
            disabled={isSubmitting || isReadOnly}
            startIcon={isSubmitting ? <CircularProgress size={24} /> : null}
          >
            {isSubmitting ? 'Bezig met aanmaken...' : 'Aanmaken'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={!!drillPointToDelete} 
        onClose={() => setDrillPointToDelete(null)} 
        maxWidth="xs"
        // Add slotProps to make backdrop transparent (consistent styling)
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'transparent',
            },
          },
        }}
      >
        <DialogTitle>Boring verwijderen?</DialogTitle>
        <DialogContent>
          <Typography>
            Weet u zeker dat u deze boring wilt verwijderen? 
            Dit kan niet ongedaan worden gemaakt.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDrillPointToDelete(null)}>
            Annuleren
          </Button>
          <Button 
            onClick={handleDeleteDrillPoint}
            color="error"
            disabled={isDeleting || isReadOnly}
            startIcon={isDeleting ? <CircularProgress size={24} /> : null}
          >
            {isDeleting ? 'Bezig met verwijderen...' : 'Verwijderen'}
          </Button>
        </DialogActions>
      </Dialog>

      <List>
        {drillPoints.map((point) => (
          <React.Fragment key={point.id}>
            <ListItem
              divider
              onClick={() => setExpandedItem(expandedItem === point.id ? null : point.id)}
              sx={{ cursor: 'pointer' }}
            >
              <ListItemText
                primary={point.name || 'Naamloos'}
              />
              <IconButton 
                edge="end" 
                color="error" 
                onClick={(e) => {
                  e.stopPropagation(); // Prevent expanding when clicking delete
                  handleOpenDeleteDialog(point);
                }}
                sx={{ mr: 1 }}
                disabled={isReadOnly}
              >
                <DeleteIcon />
              </IconButton>
              <IconButton edge="end">
                {expandedItem === point.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </ListItem>
            <Collapse in={expandedItem === point.id} timeout="auto" unmountOnExit>
              <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
                {/* ...existing form fields... */}
                <TextField
                  fullWidth
                  label="Naam"
                  margin="normal"
                  value={point.name || ''}
                  onChange={(e) => handleTextFieldChange(point, 'name', e.target.value)}
                  inputProps={{ spellCheck: 'false' }}
                  disabled={isReadOnly}
                />
                <TextField
                  fullWidth
                  label="Opmerkingen"
                  margin="normal"
                  multiline
                  rows={4}
                  value={point.remarks || ''}
                  onChange={(e) => handleTextFieldChange(point, 'remarks', e.target.value)}
                  inputProps={{ spellCheck: 'false' }}
                  disabled={isReadOnly}
                />
                <TextField
                  fullWidth
                  label="Geometrie"
                  margin="normal"
                  value={point.geometry || ''}
                  onChange={(e) => handleTextFieldChange(point, 'geometry', e.target.value)}
                  inputProps={{ spellCheck: 'false' }}
                  disabled={isReadOnly}
                />
                <DatePicker
                  label="Startdatum"
                  value={point.starttime ? new Date(point.starttime) : null}
                  onChange={(date) => handleDateChange(point, date)}
                  format="dd-MM-yyyy"
                  disabled={isReadOnly}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      margin: "normal",
                      InputProps: {
                        endAdornment: (
                          <>
                            {point.starttime && (
                              <InputAdornment position="end" sx={{ marginRight: -1.5 }}>
                                <IconButton
                                  aria-label="clear date"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDateChange(point, null);
                                  }}
                                  edge="end"
                                  size="small"
                                >
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            )}
                          </>
                        ),
                      }
                    }
                  }}
                />
                {/* Wrap Depth value and unit in a flex Box */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}> 
                  <TextField
                    sx={{ width: '50%' }} // Adjust width as needed
                    label="Diepte"
                    margin="normal"
                    type="number"
                    value={point.depth_value || ''}
                    onChange={(e) => handleTextFieldChange(point, 'depth_value', e.target.value)}
                    inputProps={{ 
                      step: "0.01",
                      spellCheck: 'false'
                    }}
                    disabled={isReadOnly}
                  />
                  {/* Render depth unit field within the Box */}
                  <Box sx={{ width: '50%' }}> 
                    {renderField(point, 'depth_uom', 'Eenheid', 'Diepte eenheid')}
                  </Box>
                </Box>
                {/* Use filtered data for depth_condition */}
                {renderField(point, 'depth_condition', 'Hoedanigheid', 'Diepte conditie')}
                {renderField(point, 'groundleveltype', 'MaaiveldType', 'Maaiveld type')}
                <TextField
                  fullWidth
                  label="EPSG code"
                  margin="normal"
                  value={point.epsg_code || ''}
                  onChange={(e) => handleTextFieldChange(point, 'epsg_code', e.target.value)}
                  inputProps={{ spellCheck: 'false' }}
                  disabled={isReadOnly}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={point.isSuccessful || false}
                      onChange={(e) => handleSwitchChange(point, e.target.checked)}
                      disabled={isReadOnly}
                    />
                  }
                  label="Succesvol"
                />
                {/* Wrap GroundLevel value and unit in a flex Box */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <TextField
                    sx={{ width: '50%' }} // Adjust width as needed
                    label="Grondwater niveau"
                    margin="normal"
                    type="number"
                    value={point.groundLevel_value || ''}
                    onChange={(e) => handleTextFieldChange(point, 'groundLevel_value', e.target.value)}
                    inputProps={{ 
                      step: "0.01",
                      spellCheck: 'false'
                    }}
                    disabled={isReadOnly}
                  />
                  {/* Render groundLevel unit field within the Box */}
                  <Box sx={{ width: '50%' }}> 
                    {renderField(point, 'groundLevel_uom', 'Eenheid', 'Eenheid')}
                  </Box>
                </Box>
                {renderField(point, 'measurementObjectType', 'MeetObjectSoort', 'Meetobject type')}
              </Box>
            </Collapse>
          </React.Fragment>
        ))}
      </List>
    </Box>
  );
}
